// src/pages/achete-terrain/Sites.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Button, Breadcrumb, Modal, message,
  Grid, Select, Tag, Card, Row, Col, Statistic, Avatar, Tooltip, Dropdown
} from 'antd';
import {
  EyeOutlined, DeleteOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownloadOutlined,
  SearchOutlined, PlusOutlined, RightOutlined,
  HomeOutlined, EnvironmentOutlined, CheckCircleOutlined,
  AppstoreOutlined, CalendarOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { acheteTerrainService } from '../../common/services';
import dayjs from 'dayjs';

const Sites = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    viabilises: 0,
    nonViabilises: 0,
    totalLots: 0
  });

  // recherche / filtres
  const [q, setQ] = useState('');
  const [viabilise, setViabilise] = useState(undefined);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const viabilises = data.filter(s => s.estViabilise).length;
    const totalLots = data.reduce((sum, s) => sum + (s.nombreTotalLots ?? s.lots?.length ?? 0), 0);

    setStats({
      total: data.length,
      viabilises,
      nonViabilises: data.length - viabilises,
      totalLots
    });
  }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        q: q || undefined,
        viabilise,
      };
      const data = await acheteTerrainService.listSites(params);

      const sitesData = Array.isArray(data) ? data : (data.items || []);
      setItems(sitesData);
      setTotal(Array.isArray(data) ? data.length : (data.total || 0));
      calculateStats(sitesData);
    } catch (e) {
      console.error('Erreur chargement sites :', e);
      message.error("Impossible de charger les sites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, q, viabilise]);

  const handleDelete = (site) => {
    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={40} style={{ backgroundColor: '#ff4d4f' }} icon={<DeleteOutlined />} />
          <span>Supprimer le site</span>
        </div>
      ),
      content: (
        <Card size="small" style={{ marginTop: 16, background: '#fff1f0', border: '1px solid #ffa39e' }}>
          <p style={{ marginBottom: 8 }}><strong>Nom :</strong> {site.nom}</p>
          <p style={{ marginBottom: 8 }}><strong>Localisation :</strong> {site.localisation}</p>
          <p style={{ marginBottom: 0, fontSize: 12, color: '#8c8c8c' }}>
            Si des lots sont vendus, utilisez la suppression forcée.
          </p>
        </Card>
      ),
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 480,
      okButtonProps: { danger: true, size: controlSize },
      cancelButtonProps: { size: controlSize },
      onOk: async () => {
        try {
          await acheteTerrainService.deleteSite(site._id);
          message.success("Site supprimé.");
          fetchList();
        } catch (e) {
          const needForce = e?.status === 409 || String(e?.message || '').includes('vendu');
          if (needForce) {
            Modal.confirm({
              title: "Suppression forcée ?",
              content: "Ce site contient des lots vendus. Confirmez la suppression forcée.",
              okText: "Forcer",
              okType: "danger",
              cancelText: "Annuler",
              centered: true,
              onOk: async () => {
                try {
                  await acheteTerrainService.deleteSite(site._id, { force: true });
                  message.success("Site supprimé (forcé).");
                  fetchList();
                } catch (err2) {
                  console.error(err2);
                  message.error("Échec de la suppression forcée.");
                }
              }
            });
          } else {
            console.error(e);
            message.error("Échec de la suppression du site.");
          }
        }
      }
    });
  };

  const handleRecompute = async (site) => {
    try {
      await acheteTerrainService.recomputeNombreLots(site._id);
      message.success("nombreTotalLots recalculé.");
      fetchList();
    } catch (e) {
      console.error(e);
      message.error("Échec du recalcul.");
    }
  };

  // Exports
  const exportToExcel = () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const rows = items.map(s => ({
        Nom: s.nom,
        Localisation: s.localisation,
        Viabilise: s.estViabilise ? 'Oui' : 'Non',
        NombreLots: s.nombreTotalLots ?? s.lots?.length ?? 0,
        CreeLe: s.createdAt ? dayjs(s.createdAt).format('DD/MM/YYYY HH:mm') : ''
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sites");
      XLSX.writeFile(workbook, "sites.xlsx");
      message.success(`${items.length} sites exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export Excel');
    } finally {
      hideLoading();
    }
  };

  const exportToCSV = () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const rows = items.map(s => ({
        Nom: s.nom,
        Localisation: s.localisation,
        Viabilise: s.estViabilise ? 'Oui' : 'Non',
        NombreLots: s.nombreTotalLots ?? s.lots?.length ?? 0,
        CreeLe: s.createdAt ? dayjs(s.createdAt).format('DD/MM/YYYY HH:mm') : ''
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'sites.csv');
      message.success(`${items.length} sites exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      hideLoading();
    }
  };

  const exportToPDF = () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Liste des sites - Achète ton terrain", 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Nom', 'Localisation', 'Viabilisé', 'Lots', 'Créé le']],
        body: items.map((s) => [
          s.nom,
          s.localisation,
          s.estViabilise ? 'Oui' : 'Non',
          s.nombreTotalLots ?? (s.lots?.length || 0),
          s.createdAt ? dayjs(s.createdAt).format('DD/MM/YYYY HH:mm') : '',
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [250, 140, 22] },
      });
      doc.save("sites.pdf");
      message.success(`${items.length} sites exportés`);
    } catch (e) {
      message.error('Erreur lors de l\'export PDF');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportToExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      label: 'Exporter CSV',
      onClick: exportToCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportToPDF
    }
  ];

  const columns = [
    {
      title: 'Site',
      key: 'site',
      width: isMobile ? 180 : 280,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={40} style={{ backgroundColor: '#fa8c16' }} icon={<HomeOutlined />} />
          <div>
            <div style={{ fontWeight: 600, color: '#262626' }}>{record.nom}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />
              {record.localisation}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Viabilisé',
      dataIndex: 'estViabilise',
      key: 'estViabilise',
      width: 110,
      align: 'center',
      render: (v) => v ? (
        <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontWeight: 500 }}>Oui</Tag>
      ) : (
        <Tag color="default">Non</Tag>
      ),
    },
    {
      title: 'Lots',
      key: 'nombreTotalLots',
      width: 100,
      align: 'center',
      render: (_, s) => (
        <Tag color="blue" style={{ fontWeight: 600, fontSize: 13 }}>
          {s?.nombreTotalLots ?? (s?.lots?.length || 0)}
        </Tag>
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      responsive: ['md'],
      render: (d) => d ? (
        <Tooltip title={dayjs(d).format('DD/MM/YYYY HH:mm:ss')}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
            {dayjs(d).format('DD/MM/YYYY HH:mm')}
          </span>
        </Tooltip>
      ) : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Tooltip title="Voir détails">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/dashboard/site/${record._id}`)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
          <Tooltip title="Recalculer lots">
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => handleRecompute(record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const onTableChange = (pagination) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
  };

  // Style des lignes alternées
  const rowClassName = (_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark';

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light { background-color: #ffffff; }
          .table-row-dark { background-color: #fff7e6; }
          .ant-table-tbody > tr:hover > td { background-color: #ffe7ba !important; }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-green {
            background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-gray {
            background: linear-gradient(135deg, #8c8c8c 0%, #bfbfbf 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-blue {
            background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title,
          .stat-card-green .ant-statistic-title,
          .stat-card-gray .ant-statistic-title,
          .stat-card-blue .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-orange .ant-statistic-content,
          .stat-card-green .ant-statistic-content,
          .stat-card-gray .ant-statistic-content,
          .stat-card-blue .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Achète ton terrain</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Sites</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><HomeOutlined style={{ marginRight: 6 }} />Total sites</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card-green" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckCircleOutlined style={{ marginRight: 6 }} />Viabilisés</span>}
                value={stats.viabilises}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card-gray" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><HomeOutlined style={{ marginRight: 6 }} />Non viabilisés</span>}
                value={stats.nonViabilises}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><AppstoreOutlined style={{ marginRight: 6 }} />Total lots</span>}
                value={stats.totalLots}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={24} md={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher nom / localisation..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                allowClear
                placeholder="Filtrer: viabilisé ?"
                value={viabilise}
                onChange={(v) => { setViabilise(v); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
              >
                <Select.Option value="true">
                  <Tag color="green" style={{ margin: 0 }}>Oui - Viabilisé</Tag>
                </Select.Option>
                <Select.Option value="false">
                  <Tag style={{ margin: 0 }}>Non - Non viabilisé</Tag>
                </Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={10} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${items.length})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={controlSize}
            scroll={{ x: 700 }}
            rowClassName={rowClassName}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} sites
                </span>
              ),
              showLessItems: isMobile,
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Sites;
